/**
 * Mixpanel analytics for the feed widget.
 *
 * Recovers the tracking that shipped with the legacy Streakers feed widget and
 * was left behind when the feed moved into this repo on 2026-08-16 — the
 * cutover that took Rush Street Interactive's feed analytics off the Mixpanel
 * project they pay for and onto Wager Studios' own impression pipeline.
 *
 * Routing follows the legacy widget exactly: Rush Street reports into the
 * project RSI pays for, every other brand into Wager's shared project. RSI is
 * sold a dashboard on theirs; the rest is Wager's own trend data.
 *
 * Every event name and property key below is reproduced verbatim from the
 * legacy implementation, spaces and capitalization included. RSI's dashboards
 * join on these exact strings, so add properties rather than repairing old
 * spellings — a rename reads as a deleted event on their end.
 *
 * Two deliberate differences from the reference implementation, each noted at
 * the code that causes it: the SDK is imported lazily (bundle budget), and only
 * production deployments report (see `resolveMixpanelToken`). The legacy
 * widget keyed on the bundler's production flag, which in this repo is true for
 * staging too.
 */

import type { OverridedMixpanel } from 'mixpanel-browser'

type MixpanelInstance = OverridedMixpanel
type MixpanelModule = { default: MixpanelInstance }

/**
 * RSI's own Mixpanel project, carried over from the legacy feed widget.
 *
 * Vite replaces `__FEED_ORG__` with a string literal at build time, so in
 * every other operator's bundle this folds to `null` and RSI's token never
 * ships: a Bally's release cannot be pointed at RSI's project by mounting it
 * with a `rushstreet` attribute. A build with no brand set keeps the token,
 * since there the mounted attribute is what decides.
 *
 * The comparisons stay literal rather than calling `isRushstreetBrand`: the
 * minifier folds `"ballys" === "rushstreet"` and does not fold a function
 * call, and only the folded form drops the string. Per-state brand names are
 * runtime attribute values, never build orgs, so these two spellings are the
 * whole set.
 */
const RSI_MIXPANEL_TOKEN: string | null =
  __FEED_ORG__ === '' || __FEED_ORG__ === 'rushstreet'
    ? '7c632918a857803bedeb4f05848f0504'
    : null

/**
 * Wager's shared project, where the legacy widget sent every brand that is not
 * Rush Street. Not an operator's project: nobody is sold a dashboard on it.
 */
const WAGER_MIXPANEL_TOKEN = '43599830b42fd1742f22ee5eec46cef4'

// Page-level, not per mount, on purpose. The SDK itself is one instance with
// one distinct_id per page, so a viewer is a property of the page and not of
// any one <wager-games-feed>. Offering follows the same rule: an operator site
// serves one offering (nj.betrivers.com is rsiusnj), and two feeds on the same
// page are two placements of that offering. The legacy widget was a
// single-page app with exactly this shape. A host that mounted two feeds for
// *different* offerings on one page would have the second relabel
// `Share Bet`; no operator's integration does that.
let brand = ''
let offering = ''
let viewerId: string | null = null
// False until the host has said who is looking, "nobody" included. Identity
// waits for it; the pageview waits only when there is a persisted punter it
// could be misattributed to. See `syncViewer`.
let viewerResolved = false
let pageviewSent = false
let configuredToken: string | null = null
let instance: MixpanelInstance | null = null
let loadPromise: Promise<MixpanelInstance | null> | null = null

/**
 * Verbatim port of the legacy brand predicate. This repo
 * builds Rush Street bundles as `FEED_ORG=rushstreet`, but the legacy widget
 * also saw per-state brand names (`rushstreet_nj`, `rushstreet-mi`) and the
 * bare `rsi` alias, so all four shapes stay recognized.
 */
export function isRushstreetBrand(value?: string | null): boolean {
  const brandName = value?.toLowerCase()
  return (
    brandName === 'rsi' ||
    brandName === 'rushstreet' ||
    brandName?.startsWith('rushstreet_') === true ||
    brandName?.startsWith('rushstreet-') === true
  )
}

/**
 * Which project a mount reports into, or `null` for none. Same split as the
 * legacy widget: Rush Street to RSI's project, everyone else to Wager's.
 */
export function resolveMixpanelToken(
  organization: string,
  deployEnvironment: string,
): string | null {
  // `import.meta.env.PROD` would be the obvious test and is the wrong one: it
  // reports the bundler's optimisation mode, which `vite build` sets for the
  // staging rollout as well. Reading it would point every staging bundle at a
  // live project, mixing our rehearsals into RSI's dashboards and Wager's
  // trend data. The deployment target is passed in instead, which also leaves
  // both halves of the gate testable without building a bundle.
  if (deployEnvironment !== 'production') return null
  // The legacy widget always had a brand baked in at build time. Here the
  // brand can arrive as a mount attribute, and a mount without one cannot
  // fetch a surface either, so it has nothing worth stamping `Brand: ''` on.
  if (!organization) return null
  return isRushstreetBrand(organization) ? RSI_MIXPANEL_TOKEN : WAGER_MIXPANEL_TOKEN
}

export function isTrackingDisabledByHost(): boolean {
  return typeof window !== 'undefined'
    && (window as Window & { __WAGER_FEED_DISABLE_TRACKING__?: boolean }).__WAGER_FEED_DISABLE_TRACKING__ === true
}

/**
 * Point the widget at the mounted brand's project and start loading the SDK.
 * Safe to call on every render pass: re-initializing with an unchanged token
 * is a no-op, which matters because the custom element re-renders on any
 * attribute change.
 */
export function initFeedMixpanel(organization: string, feedOffering?: string): void {
  // A preview host (Studio's feed preview, #551) runs the brand's production
  // bundle with real viewers absent; its page sets this before the bundle
  // loads so operator pageviews and simulated bets never reach the brand's
  // live project. Releases older than this flag keep reporting.
  if (isTrackingDisabledByHost()) return
  const token = resolveMixpanelToken(organization, __FEED_ENV__)
  if (!token) return

  // The SDK keeps one primary instance per page and refuses to re-init it
  // against another token, so the first project a page arms is the one it
  // keeps. Bundles are built per brand and an operator's site embeds its own,
  // so two projects on one page does not occur in a deployment. If a test host
  // ever does it, say so rather than silently relabel the first project's
  // events with the second brand.
  if (configuredToken && token !== configuredToken) {
    if (import.meta.env.DEV) {
      console.warn('[wager-feed] Mixpanel is already bound to another project; ignoring', organization)
    }
    return
  }

  brand = organization
  offering = feedOffering || ''

  if (configuredToken) return

  configuredToken = token
  // Load now rather than on the first tracked interaction. The SDK is emitted
  // as its own async chunk either way, so this costs the bundle budget
  // nothing — but it keeps `track_pageview` honest: a viewer who scrolls the
  // feed without touching it still counts as a pageview, exactly as it did on
  // the legacy widget's eager import.
  void loadMixpanel()
}

/**
 * Bind subsequent events to the operator's punter, or to nobody. Mirrors the
 * legacy `setUserId` path, which called `identify` as soon as the viewer
 * resolved. Callers must wait for the host's answer before calling this with
 * an absent viewer: an absent viewer here means logged out, not "not yet".
 */
export function identifyFeedViewer(userId?: string | null): void {
  const nextViewerId = userId || null
  if (viewerResolved && nextViewerId === viewerId) return
  viewerResolved = true
  viewerId = nextViewerId
  void withMixpanel(syncViewer)
}

/**
 * Make the SDK's identity match the host's answer, then send the pageview.
 *
 * The SDK persists the last identified punter across page loads. The legacy
 * widget never cleared it, so on a shared device the next person's pre-login
 * activity, the pageview included, landed on the previous punter's profile.
 * The persisted `$user_id` is the tell: identify registers it and reset
 * clears it, so its presence with no current viewer, or with a different one,
 * means a stale identity. Reset first, then identify, so the SDK never asks
 * the server to merge one punter's id into another's.
 *
 * The pageview is sent here rather than by `track_pageview: true` at init
 * because init runs before the host has answered. It waits for that answer
 * only when there is a persisted punter for it to land on: with nothing
 * persisted the page is anonymous, the pageview goes out on the device id
 * as soon as the chunk lands, and a later `identify` merges it, which is the
 * SDK's ordinary login flow. Holding it unconditionally would tie every
 * pageview to the betting provider answering, and on a Kambi surface where
 * the client stalls that answer never comes. Idempotent, so it can run both
 * when the chunk lands and on every identity change after that.
 */
function syncViewer(mixpanel: MixpanelInstance): void {
  const persistedViewer = mixpanel.get_property('$user_id') as string | undefined
  if (viewerResolved) {
    if (viewerId) {
      if (persistedViewer && persistedViewer !== viewerId) mixpanel.reset()
      mixpanel.identify(viewerId)
    } else if (persistedViewer) {
      mixpanel.reset()
    }
  } else if (persistedViewer) {
    return
  }
  if (!pageviewSent) {
    pageviewSent = true
    mixpanel.track_pageview()
  }
}

/** The offering the feed is mounted against; `Share Bet` reports it. */
export function feedOffering(): string {
  return offering || 'unknown'
}

/**
 * `User ID` and `Brand` rode on nearly every legacy event, so they are applied
 * here instead of at each call site. Callers may override `User ID`; `Brand`
 * always reflects the mounted organization.
 */
export function trackFeedEvent(
  name: string,
  properties: Record<string, unknown> = {},
): void {
  void withMixpanel((mixpanel) => {
    mixpanel.track(name, {
      'User ID': viewerId,
      ...properties,
      Brand: brand,
    })
  })
}

async function loadMixpanel(): Promise<MixpanelInstance | null> {
  if (!configuredToken) return null
  if (instance) return instance

  if (!loadPromise) {
    const token = configuredToken
    // The package's default entry bundles the session recorder and every
    // optional module (~125 KB gzip). Nothing here records sessions or captures
    // heatmaps, so the core loader ships just the SDK as one small chunk. Core
    // rather than the async-modules loader on purpose: that one fetches the
    // recorder from Mixpanel's CDN on demand, so a config change (or an SDK
    // upgrade that flips a default) could inject a session recorder into an
    // operator's page; core throws instead. The specifier stays a literal so
    // Vite can emit the chunk.
    loadPromise = (import(
      'mixpanel-browser/src/loaders/loader-module-core'
    ) as unknown as Promise<MixpanelModule>)
      .then(({ default: mixpanel }) => {
        mixpanel.init(token, {
          debug: import.meta.env.DEV,
          // Sent from `syncViewer` once the identity is known instead.
          track_pageview: false,
          // Legacy parity: the Streakers widget set `ignore_dnt: import.meta.env.PROD`,
          // and only production builds get this far (see `resolveMixpanelToken`).
          ignore_dnt: true,
          persistence: 'localStorage',
        })
        syncViewer(mixpanel)
        instance = mixpanel
        return mixpanel
      })
      .catch((error): MixpanelInstance | null => {
        // Analytics is never load-bearing for the feed, so this stays silent in
        // production — but a swallowed load failure is indistinguishable from
        // "no events yet", so say so while developing.
        if (import.meta.env.DEV) {
          console.warn('[wager-feed] Mixpanel failed to load', error)
        }
        loadPromise = null
        return null
      })
  }

  return loadPromise
}

async function withMixpanel(
  callback: (mixpanel: MixpanelInstance) => void,
): Promise<void> {
  const mixpanel = await loadMixpanel()
  if (!mixpanel) return
  try {
    callback(mixpanel)
  } catch (error) {
    // A rejected event must never surface as a broken card. Same rule as the
    // load path above: silent in production, said out loud while developing,
    // so a malformed payload does not read as "no events yet".
    if (import.meta.env.DEV) {
      console.warn('[wager-feed] Mixpanel call failed', error)
    }
  }
}

/** Test seam: drops SDK state so each case starts from a clean instance. */
export function resetFeedMixpanelForTests(): void {
  brand = ''
  offering = ''
  viewerId = null
  viewerResolved = false
  pageviewSent = false
  configuredToken = null
  instance = null
  loadPromise = null
}
